import { useEffect, useState } from "react";
import api from "../lib/api";
import { formatDate } from "../lib/format";
import { Trash2, Plus, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const ROLES = ["pending", "user", "admin"];

export default function Usuarios() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [allowed, setAllowed] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("user");

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/users").then(r => setUsers(r.data)),
      api.get("/allowed-emails").then(r => setAllowed(r.data)),
      api.get("/audit-log?limit=50").then(r => setAudit(r.data)),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const addAllowed = async (e) => {
    e?.preventDefault();
    if (!newEmail.trim()) return;
    try {
      await api.post("/allowed-emails", { email: newEmail.trim(), role: newRole });
      toast.success(`Acceso permitido a ${newEmail}`);
      setNewEmail("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const removeAllowed = async (email) => {
    if (!window.confirm(`¿Quitar a ${email} de la lista de emails autorizados? Si tenía cuenta, su sesión seguirá hasta que expire.`)) return;
    try {
      await api.delete(`/allowed-emails/${encodeURIComponent(email)}`);
      toast.success("Quitado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const updateUser = async (uid, patch) => {
    try {
      await api.patch(`/users/${uid}`, patch);
      toast.success("Actualizado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al actualizar");
    }
  };

  const remove = async (uid) => {
    if (!window.confirm("¿Eliminar este usuario? Se cerrarán sus sesiones.")) return;
    try {
      await api.delete(`/users/${uid}`);
      toast.success("Usuario eliminado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="p-8 max-w-5xl" data-testid="users-page">
      <div className="mb-8">
        <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)]">Administración</div>
        <h1 className="font-display text-4xl font-black tracking-tight">Usuarios y permisos.</h1>
        <p className="text-sm text-[var(--ink-muted)] mt-2 max-w-2xl">
          Aprueba accesos, asigna roles y revoca usuarios. Nuevos usuarios entran como «pending» hasta que un administrador les active.
        </p>
      </div>

      <div className="border border-[var(--border)] bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[var(--hover)] text-[10px] uppercase tracking-widest font-mono-num text-[var(--ink-muted)]">
            <tr>
              <th className="text-left px-4 py-3">Usuario</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Rol</th>
              <th className="text-left px-4 py-3">Activo</th>
              <th className="text-left px-4 py-3">Alta</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" className="px-4 py-6 text-center text-[var(--ink-muted)]">Cargando…</td></tr>
            ) : users.map(u => {
              const isMe = me?.user_id === u.user_id;
              return (
                <tr key={u.user_id} className="border-t border-[var(--border)] hover:bg-[var(--hover)]" data-testid={`user-row-${u.user_id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {u.picture ? (
                        <img src={u.picture} alt="" className="h-8 w-8 object-cover" />
                      ) : (
                        <div className="h-8 w-8 bg-[var(--hover)] flex items-center justify-center text-xs font-bold">
                          {u.name?.[0] || "U"}
                        </div>
                      )}
                      <span>{u.name} {isMe && <span className="text-[10px] uppercase font-mono-num ml-1 text-[var(--ink-muted)]">(tú)</span>}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono-num text-xs">{u.email}</td>
                  <td className="px-4 py-3">
                    <select
                      data-testid={`user-role-${u.user_id}`}
                      className="border border-[var(--border)] px-2 py-1 text-sm font-mono-num focus:outline-none focus:border-black"
                      value={u.role}
                      disabled={isMe}
                      onChange={(e) => updateUser(u.user_id, { role: e.target.value })}
                    >
                      {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!u.is_active}
                        disabled={isMe}
                        onChange={(e) => updateUser(u.user_id, { is_active: e.target.checked })}
                        data-testid={`user-active-${u.user_id}`}
                      />
                      <span className="text-xs">{u.is_active ? "Sí" : "No"}</span>
                    </label>
                  </td>
                  <td className="px-4 py-3 font-mono-num text-xs text-[var(--ink-muted)]">{formatDate(u.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    {!isMe && (
                      <button onClick={() => remove(u.user_id)} className="text-[var(--ink-muted)] hover:text-red-700" data-testid={`user-delete-${u.user_id}`}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ---------- Allowed emails (whitelist) ---------- */}
      <div className="mt-12 mb-6">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck size={16} />
          <h2 className="font-display text-2xl font-black tracking-tight">Emails autorizados</h2>
        </div>
        <p className="text-sm text-[var(--ink-muted)] max-w-2xl">
          Solo los emails de esta lista pueden iniciar sesión. Cualquier otro intento queda bloqueado y registrado.
        </p>
      </div>

      <form onSubmit={addAllowed} className="flex flex-wrap gap-2 mb-4" data-testid="add-allowed-form">
        <input
          type="email"
          placeholder="email@ejemplo.com"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          className="border border-[var(--border)] px-3 py-2 text-sm font-mono-num flex-1 min-w-[260px] focus:outline-none focus:border-black"
          data-testid="new-allowed-email"
        />
        <select
          value={newRole}
          onChange={(e) => setNewRole(e.target.value)}
          className="border border-[var(--border)] px-3 py-2 text-sm font-mono-num focus:outline-none focus:border-black"
          data-testid="new-allowed-role"
        >
          <option value="user">user</option>
          <option value="admin">admin</option>
        </select>
        <button type="submit" className="bg-black text-white px-4 py-2 text-sm inline-flex items-center gap-2 hover:bg-gray-800" data-testid="add-allowed-submit">
          <Plus size={14} /> Permitir
        </button>
      </form>

      <div className="border border-[var(--border)] bg-white mb-12">
        <table className="w-full text-sm">
          <thead className="bg-[var(--hover)] text-[10px] uppercase tracking-widest font-mono-num text-[var(--ink-muted)]">
            <tr>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Rol</th>
              <th className="text-left px-4 py-3">Añadido por</th>
              <th className="text-left px-4 py-3">Fecha</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {allowed.map(a => (
              <tr key={a.email} className="border-t border-[var(--border)] hover:bg-[var(--hover)]" data-testid={`allowed-row-${a.email}`}>
                <td className="px-4 py-3 font-mono-num text-xs">{a.email}</td>
                <td className="px-4 py-3"><span className="text-xs font-mono-num uppercase">{a.role}</span></td>
                <td className="px-4 py-3 text-xs text-[var(--ink-muted)]">{a.added_by || "—"}</td>
                <td className="px-4 py-3 font-mono-num text-xs text-[var(--ink-muted)]">{formatDate(a.added_at)}</td>
                <td className="px-4 py-3 text-right">
                  {a.email !== "eduardo@grabarte360.com" && (
                    <button onClick={() => removeAllowed(a.email)} className="text-[var(--ink-muted)] hover:text-red-700" data-testid={`allowed-delete-${a.email}`}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ---------- Audit log ---------- */}
      <div className="mb-6">
        <h2 className="font-display text-2xl font-black tracking-tight">Actividad reciente</h2>
        <p className="text-sm text-[var(--ink-muted)]">Últimos 50 eventos de seguridad y acciones destructivas.</p>
      </div>
      <div className="border border-[var(--border)] bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[var(--hover)] text-[10px] uppercase tracking-widest font-mono-num text-[var(--ink-muted)]">
            <tr>
              <th className="text-left px-4 py-2">Fecha</th>
              <th className="text-left px-4 py-2">Email</th>
              <th className="text-left px-4 py-2">Acción</th>
              <th className="text-left px-4 py-2">IP</th>
              <th className="text-left px-4 py-2">Detalles</th>
            </tr>
          </thead>
          <tbody>
            {audit.length === 0 ? (
              <tr><td colSpan="5" className="px-4 py-6 text-center text-[var(--ink-muted)] text-xs">Sin actividad registrada todavía.</td></tr>
            ) : audit.map((a, i) => (
              <tr key={i} className="border-t border-[var(--border)]">
                <td className="px-4 py-2 font-mono-num text-xs text-[var(--ink-muted)]">{formatDate(a.ts)}</td>
                <td className="px-4 py-2 font-mono-num text-xs">{a.email || "—"}</td>
                <td className="px-4 py-2 text-xs">
                  <span className={`px-2 py-0.5 ${a.action === 'login_denied' ? 'bg-red-100 text-red-800' : a.action === 'login' ? 'bg-emerald-100 text-emerald-800' : 'bg-zinc-100'}`}>
                    {a.action}
                  </span>
                </td>
                <td className="px-4 py-2 font-mono-num text-xs text-[var(--ink-muted)]">{a.ip || "—"}</td>
                <td className="px-4 py-2 text-xs text-[var(--ink-muted)]">{a.details ? JSON.stringify(a.details) : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
